import { useCallback, useEffect, useMemo } from "react";
import { BooleanParam, StringParam, useQueryParam, } from "use-query-params";
import { usePersistantObject, usePersistentBoolean, usePersistentString, } from ".";
// Takes `key` which uses `useHydratedPersistantString` to get the "stringKey"
// for a given value and `getValue` which should be a useCallback hook.
// Hydrates a <T[]> value using "stringKey with getValue. The query param value
// should be an array of strings that are joined with a ','
//
// This method exists in order to use queryParams to store a complex array of
// objects that's dependent on an API call or is otherwise expensive to store
// in the URL directly.
export const useKeyedHydratedPersistantArray = (key, paramName, getValue) => {
    const [stringKey, setKeyLocal] = useHydratedPersistantString(key, paramName);
    const t = useMemo(() => {
        if (stringKey === undefined) {
            return undefined;
        }
        return getValue(stringKey.split(","));
    }, [stringKey, getValue]);
    const setKeys = useCallback(keys => {
        setKeyLocal(old => {
            let nu = undefined;
            if (typeof keys === "function") {
                nu = keys(old?.split(","));
            }
            else {
                nu = keys;
            }
            return nu?.join(",");
        });
    }, [setKeyLocal]);
    return [t, setKeys];
};
// Takes `key` which uses `useHydratedPersistantString` to get the "stringKey"
// for a given value and `getValue` which should be a useCallback hook.
// Hydrates a <T> value using "stringKey with getValue.
//
// This method exists in order to use queryParams to store a complex object
// that's dependent on an API call or is otherwise expensive to store in the
// URL directly.
export const useKeyedHydratedPersistantObject = (key, paramName, getValue, onSet, settings) => {
    const [stringKey, setKeyLocal] = useHydratedPersistantString(key, paramName, undefined, settings);
    const t = useMemo(() => {
        if (stringKey === undefined) {
            return undefined;
        }
        return getValue(stringKey);
    }, [stringKey, getValue]);
    const setKey = useCallback(key => {
        setKeyLocal(old => {
            let nu = undefined;
            if (typeof key === "function") {
                nu = key(old);
            }
            else {
                nu = key;
            }
            const nuT = getValue(nu);
            onSet && onSet(nuT);
            return nu;
        });
    }, [setKeyLocal, getValue, onSet]);
    return [t, setKey];
};
export const useHydratedPersistantBoolean = (key, paramName, initialValue) => {
    const [value, setValue] = usePersistentBoolean(key, initialValue);
    const [param, setParam] = useQueryParam(paramName, BooleanParam);
    useEffect(() => {
        if (param !== null && param !== undefined) {
            setValue(param);
            setParam(undefined, "replaceIn");
        }
    }, [param, setParam, setValue]);
    const hydrated = useMemo(() => (param === undefined || param === null ? value : param), [param, value]);
    return [hydrated, setValue];
};
const useHydratedValue = (value, setValue, param, setParam, settings) => {
    useEffect(() => {
        if (param !== null && param !== undefined) {
            setValue(param);
            if (!settings?.keepParam) {
                setParam(undefined, "replaceIn");
            }
        }
    }, [param, setParam, setValue, settings]);
    const hydrated = useMemo(() => {
        return param || value;
    }, [param, value]);
    return [hydrated, setValue];
};
const useHydratedObject = (value, setValue, paramName, qpConfig) => {
    const [param, setParam] = useQueryParam(paramName, qpConfig);
    return useHydratedValue(value, setValue, param, setParam);
};
export const useHydratedPersistantObject = (key, paramName, qpConfig, defaultValue) => {
    const [value, setValue] = usePersistantObject(key, defaultValue);
    return useHydratedObject(value, setValue, paramName, qpConfig);
};
const useHydratedString = (value, setValue, paramName, settings) => {
    const [param, setParam] = useQueryParam(paramName, StringParam);
    return useHydratedValue(value, setValue, param, setParam, settings);
};
export const useHydratedPersistantString = (key, paramName, initialValue, settings) => {
    const [value, setValue] = usePersistentString(key, initialValue);
    return useHydratedString(value, setValue, paramName, settings);
};
